'use client';

import React, { useState } from 'react';
import Link from 'next/link';

const navLinks = [
    { label: 'How It Works', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Why Us', href: '#how-it-works' },
];

export default function Navigation({ isFixed = true }: { isFixed?: boolean }) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        if (href.startsWith('#')) {
            e.preventDefault();
            const element = document.querySelector(href);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                setIsMobileMenuOpen(false);
            }
        }
    };

    return (
        <nav className={`${isFixed ? 'fixed top-0' : 'relative'} w-full z-[999] bg-white/95 backdrop-blur-md border-b border-gray-100`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
                        <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-200">
                            <span className="text-white font-bold text-sm">W</span>
                        </div>
                        <span className="text-xl font-extrabold text-gray-900">Waitlistly</span>
                    </Link>

                    {/* Desktop Navigation - Centered */}
                    <div className="hidden lg:flex items-center justify-center flex-1">
                        <div className="flex items-center gap-8">
                            {navLinks.map((link) => (
                                link.href.startsWith('#') ? (
                                    <a
                                        key={link.label}
                                        href={link.href}
                                        onClick={(e) => handleSmoothScroll(e, link.href)}
                                        className="text-base text-gray-600 hover:text-gray-900 transition-colors font-medium"
                                    >
                                        {link.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={link.label}
                                        href={link.href}
                                        className="text-base text-gray-600 hover:text-gray-900 transition-colors font-medium"
                                    >
                                        {link.label}
                                    </Link>
                                )
                            ))}
                        </div>
                    </div>

                    {/* Desktop Auth Buttons */}
                    <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
                        <Link
                            href="/login"
                            prefetch={true}
                            className="text-sm font-medium text-gray-700 hover:text-gray-900 px-4 py-2 rounded-md border border-gray-200 hover:bg-gray-50 transition-all"
                        >
                            Sign in
                        </Link>
                        <Link
                            href="/signup"
                            prefetch={true}
                            className="text-sm font-semibold bg-violet-600 text-white px-5 py-2 rounded-md hover:bg-violet-700 transition-all shadow-sm shadow-violet-600/20 hover:shadow-md hover:-translate-y-0.5"
                        >
                            Get Started
                        </Link>
                    </div>

                    {/* Mobile Menu Button - Modern */}
                    <div className="lg:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
                            aria-label="Toggle menu"
                        >
                            <div className="w-5 flex flex-col justify-center items-center gap-1.5">
                                <span className={`block h-0.5 w-full bg-gray-600 rounded-full transform transition-all duration-300 origin-center ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                                <span className={`block h-0.5 w-full bg-gray-600 rounded-full transition-all duration-200 ${isMobileMenuOpen ? 'opacity-0 scale-0' : ''}`} />
                                <span className={`block h-0.5 w-full bg-gray-600 rounded-full transform transition-all duration-300 origin-center ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu - Modern Slide Down */}
            <div className={`lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl transition-all duration-300 ease-out ${isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}>
                <div className="px-6 py-6 space-y-1">
                    {/* Centered Nav Links */}
                    <div className="flex flex-col items-center gap-2 pb-4 border-b border-gray-100">
                        {navLinks.map((link) => (
                            link.href.startsWith('#') ? (
                                <a
                                    key={link.label}
                                    href={link.href}
                                    onClick={(e) => handleSmoothScroll(e, link.href)}
                                    className="w-full text-center py-3 text-base font-medium text-gray-900 hover:text-violet-600 hover:bg-violet-50 rounded-xl transition-all"
                                >
                                    {link.label}
                                </a>
                            ) : (
                                <Link
                                    key={link.label}
                                    href={link.href}
                                    className="w-full text-center py-3 text-base font-medium text-gray-900 hover:text-violet-600 hover:bg-violet-50 rounded-xl transition-all"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            )
                        ))}
                    </div>

                    {/* Auth Buttons */}
                    <div className="pt-4 space-y-3">
                        <Link
                            href="/login"
                            className="block w-full text-center py-2.5 text-base font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-md transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Sign in
                        </Link>
                        <Link
                            href="/signup"
                            className="block w-full text-center py-2.5 text-base font-semibold bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors shadow-sm shadow-violet-600/20"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Get Started
                        </Link>
                    </div>
                </div>
            </div>
        </nav>
    );
}
